import { useState, useEffect, useCallback } from 'react';
import { User, Save, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { ApiService } from '../services/apiService';
import { useViewMode } from '../hooks/useViewMode';

const EXPERTISE_OPTIONS = ['General', 'Layperson', 'Student', 'Practitioner', 'Expert'];
const DEPTH_OPTIONS = ['Brief', 'Standard', 'Deep'];

export const UserProfilePanel: React.FC = () => {
  const { viewMode } = useViewMode();
  const [isOpen, setIsOpen] = useState(false);
  const [profile, setProfile] = useState<any>(null);
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [jurisdiction, setJurisdiction] = useState('');
  const [expertise, setExpertise] = useState('General');
  const [depth, setDepth] = useState('Standard');
  const [practiceAreas, setPracticeAreas] = useState('');

  const apiService = ApiService.getInstance();

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.getUserProfile();
      setExists(result.exists);
      if (result.profile) {
        setProfile(result.profile);
        setJurisdiction(result.profile.defaultJurisdiction || '');
        setExpertise(result.profile.expertise || 'General');
        setDepth(result.profile.preferredDepth || 'Standard');
        setPracticeAreas((result.profile.practiceAreas || []).join(', '));
      }
    } catch {
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [apiService]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const areas = practiceAreas.split(',').map(a => a.trim()).filter(a => a.length > 0);
      const result = await apiService.saveUserProfile({
        defaultJurisdiction: jurisdiction || undefined,
        expertise,
        preferredDepth: depth,
        practiceAreas: areas
      });
      setProfile(result.profile);
      setExists(true);
    } catch {
      setError('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Delete your research profile? This cannot be undone.')) return;
    try {
      await apiService.deleteUserProfile();
      setProfile(null);
      setExists(false);
      setJurisdiction('');
      setExpertise('General');
      setDepth('Standard');
      setPracticeAreas('');
    } catch {
      setError('Failed to delete profile');
    }
  };

  // Fetch on first open
  useEffect(() => {
    if (isOpen && !profile && !loading) {
      fetchProfile();
    }
  }, [isOpen, profile, loading, fetchProfile]);

  if (viewMode !== 'admin') return null;

  return (
    <div data-component="UserProfilePanel" className="mb-4 border border-indigo-200 rounded-lg bg-indigo-50">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-4 py-2 text-sm font-medium text-indigo-800 hover:bg-indigo-100 rounded-t-lg"
      >
        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <User className="w-4 h-4" />
        Research Profile (V2 Personalization)
      </button>

      {isOpen && (
        <div data-flow="open" className="px-4 pb-4 space-y-3">
          {loading && <div data-flow="loading" className="text-sm text-gray-500">Loading profile...</div>}
          {error && <div data-flow="error" className="text-sm text-red-600">{error}</div>}

          {!loading && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Default Jurisdiction</label>
                <input
                  type="text"
                  value={jurisdiction}
                  onChange={(e) => setJurisdiction(e.target.value)}
                  placeholder="e.g., California"
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Expertise Level</label>
                <select
                  value={expertise}
                  onChange={(e) => setExpertise(e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                >
                  {EXPERTISE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Response Depth</label>
                <select
                  value={depth}
                  onChange={(e) => setDepth(e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                >
                  {DEPTH_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Practice Areas (comma-separated)</label>
                <input
                  type="text"
                  value={practiceAreas}
                  onChange={(e) => setPracticeAreas(e.target.value)}
                  placeholder="e.g., Employment Law, IP, Contracts"
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          )}

          {!loading && profile?.recentTopics?.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Recent Topics</label>
              <div className="flex flex-wrap gap-1">
                {profile.recentTopics.map((topic: string, i: number) => (
                  <span key={i} className="px-2 py-0.5 text-xs bg-indigo-100 text-indigo-700 rounded">{topic}</span>
                ))}
              </div>
            </div>
          )}

          {!loading && (
            <div className="flex gap-2 pt-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1 px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
              >
                <Save className="w-3 h-3" />
                {saving ? 'Saving...' : 'Save Profile'}
              </button>
              {exists && (
                <button
                  onClick={handleDelete}
                  className="flex items-center gap-1 px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                >
                  <Trash2 className="w-3 h-3" />
                  Delete
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
